'use client';

/**
 * 空态 / 错误态统一组件（v0.8 体验强化包）：
 * 无数据、加载失败时显示统一插画 + 标题 + 说明 + 可选重试按钮，
 * 双端深浅色自适应。替换全站零散的「暂无数据」文本。
 */

interface EmptyStateProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** 紧凑模式：用于卡片内小空间（列表底部等） */
  compact?: boolean;
  /** 错误态样式（红色强调图标） */
  error?: boolean;
}

export default function EmptyState({
  title,
  description,
  actionLabel,
  onAction,
  compact,
  error,
}: EmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center text-center ${
        compact ? 'gap-2 py-6' : 'gap-3 py-12'
      }`}
    >
      {/* 插画：环形 + 中心点（无数据） / 警示环（错误），跟随主题色 */}
      <svg
        viewBox="0 0 64 64"
        className={compact ? 'h-10 w-10' : 'h-14 w-14'}
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="32"
          cy="32"
          r="26"
          style={{ stroke: error ? 'rgb(var(--gf-negative))' : 'rgb(var(--gf-border))' }}
          strokeWidth="3"
          strokeDasharray="10 7"
          strokeLinecap="round"
        />
        <circle
          cx="32"
          cy="32"
          r="9"
          style={{ fill: error ? 'rgb(var(--gf-negative))' : 'rgb(var(--gf-muted))' }}
          opacity={error ? 0.85 : 0.45}
        />
        {!error && (
          <>
            <circle cx="15" cy="18" r="2.5" style={{ fill: 'rgb(var(--gf-accent))' }} opacity="0.6" />
            <circle cx="50" cy="46" r="2" style={{ fill: 'rgb(var(--gf-accent))' }} opacity="0.35" />
            <circle cx="49" cy="17" r="1.5" style={{ fill: 'rgb(var(--gf-muted))' }} opacity="0.5" />
          </>
        )}
      </svg>
      <div>
        <div className={`font-medium text-gf-text ${compact ? 'text-sm' : 'text-base'}`}>{title}</div>
        {description && (
          <div className={`mt-1 text-gf-muted ${compact ? 'text-xs' : 'text-sm'}`}>{description}</div>
        )}
      </div>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="btn-ghost mt-1 !px-4 !py-1.5 text-xs"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
